<template>
    <div class="main-layout__container">
        <div class="content__header content__header--one-item-per-line">
            <div class="content-header__back">
                <UIBackButton
                    :text="'К списку лотов для отчета'"
                    :to="router.resolve({ name: 'BidReportList' }).href"
                />
            </div>

            <div class="content-header__title">Отчет по ставкам на лот №{{ auctionId }}</div>

            <div class="content-header__background content-header__background--add-edit-page"></div>
        </div>

        <UILoader v-if="isAuctionLoading" class="detailable" />

        <template v-else>
            <div class="content__body">
                <DataTable
                    :headers="headers"
                    :data="reportBids"
                >
                    <template #index="{ rowIndex }">
                        {{ rowIndex + 1 }}
                    </template>

                    <template #value="{ row }">
                        {{ numberFormat(row.value) }} ₽
                    </template>

                    <template #created_at="{ row }">
                        {{ formatFromUTC(row.created_at) }}
                    </template>

                    <template #user="{ row }">
                        <div class="data-table__multiple-row">
                            <div>{{ row.user.full_name }}</div>
                            <div>{{ row.user.email }}</div>
                        </div>
                    </template>

                    <template #dealer="{ row }">
                        <div class="data-table__multiple-row">
                            <div>{{ row.dealer.name }}</div>
                            <div>{{ row.dealer.sap_code }}</div>
                        </div>
                    </template>
                </DataTable>
            </div>

            <div class="content__footer">
                <div class="report__button">
                    <UISimpleButton
                        :loading="downloadLoading"
                        @clicked="downloadReport"
                    >
                        <template #icon>
                            <img v-svg-inline src="@/assets/icons/report.svg" />
                        </template>
                        Экспорт в XLSX
                    </UISimpleButton>
                </div>
            </div>
        </template>
    </div>
</template>

<script lang="ts" setup>
    import { onMounted, ref } from 'vue';
    import { useRouter, useRoute } from 'vue-router';

    import { ReportBid } from '@/types/report.type.ts';

    import useDocumentDownloader from '@/utils/useDocumentDownloader.ts';
    import useLoading from '@/utils/useLoading.ts';
    import useReportRepository from '@/repositories/report/useReportRepository.ts';

    import { formatFromUTC } from '@/utils/DateTimeFormatter.ts';
    import { numberFormat } from '@/utils/NumberFormatter.ts';

    import DataTable from '@/components/shared/DataTable/DataTable.vue';

    import UIBackButton from '@/components/ui/UIBackButton/UIBackButton.vue';
    import UILoader from '@/components/ui/UILoader/UILoader.vue';
    import UISimpleButton from '@/components/ui/UISimpleButton/UISimpleButton.vue';

    const router = useRouter();
    const route = useRoute();
    const { getBidReportAuction } = useReportRepository();
    const { downloadBidReport } = useDocumentDownloader();
    const { isLoading: isAuctionLoading, setLoading: setAuctionLoading } = useLoading();

    const reportBids = ref<ReportBid[]>([]);
    const downloadLoading = ref<boolean>(false);
    const headers = ref([
        { label: '№', key: 'index', sortable: false },
        { label: 'Ставка', key: 'value', sortable: false },
        { label: 'Дата ставки', key: 'created_at', sortable: false },
        { label: 'Пользователь', key: 'user', sortable: false },
        { label: 'Дилер', key: 'dealer', sortable: false },
    ]);

    const auctionId = route.params.auction_id as unknown as number;

    function downloadReport(): void {
        downloadLoading.value = true;
        downloadBidReport(auctionId, `report-by-bids-in-lot-${auctionId}.xlsx`)
            .finally(() => {
                downloadLoading.value = false;
            });
    }

    function fetchAuction(): void {
        setAuctionLoading(true);

        getBidReportAuction(auctionId).then((fetchedBids: ReportBid[]) => {
            reportBids.value = fetchedBids;
            setAuctionLoading(false);
        });
    }

    onMounted(() => {
        fetchAuction();
    });
</script>

<style scoped lang="scss">
    @use './BidReportDetail.scss' as *;
</style>
